// Dashboard Overview Page — v2 redesign.
//
// Design ref: design_handoff_admin_ui_upgrade/screens/01-dashboard-v2.html +
// preview-dashboard.png + README Screen 1.
//
// Layout (top → bottom):
//   1. AttentionRequired banner  — expiring licenses (≤30d) + failed jobs (≤24h)
//   2. 4 MetricGroup cards        — Fleet / Licensing / Revenue / Operations
//   3. BarChart (7-day) + License Status card  (1.6fr / 1fr)
//   4. Live Activity feed         — merged jobs + validation events
//
// All numbers come from real endpoints; nothing is fabricated. On a failed load
// we leave stats null and the corresponding metrics read 0 / "—".
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { apiRequest } from '@/lib/supabase';
import { PRODUCT_NAME } from '@/lib/brand';
import { Card } from '@/components/ui/card';
import { AlertBanner } from '@/components/shared/AlertBanner';
import { Kpi } from '@/components/shared/Kpi';
import { AttnRow } from '@/components/shared/AttnRow';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import {
  Server, Key, CreditCard, Terminal, RefreshCw, Plus,
  CheckCircle, XCircle, Clock, RefreshCw as RefreshIcon, ArrowRight,
} from 'lucide-react';
import { getSaasDetails, getOrgMetrics, getOpportunities, type SaasLicenseDetailsRow, type TrustOrgMetricsRow, type TrustUpsellOpportunityRow } from '@/lib/api/trust';

// ── API contracts ───────────────────────────────────────────────────────
interface DashboardStats {
  totalLicenses: number;
  activeLicenses: number;
  expiredLicenses: number;
  revokedLicenses: number;
  suspendedLicenses: number;
  trialLicenses: number;
  totalOrganizations: number;
  activeOrganizations: number;
  churnAtRisk: number;
  validationsToday: number;
  monthlyRevenue: number;
}

interface FleetOverview {
  totals: { instances: number; healthy: number; stale: number };
  version_distribution: Array<{ version: string; count: number }>;
}

interface ValidationDay { date: string; validation_count: number; }

interface LicenseRow {
  id: string;
  license_id?: string;
  tier?: string;
  status?: string;
  expires_at?: string | null;
  organization?: { name?: string } | null;
  org_name?: string | null;
}

interface Job {
  id: string;
  instance_id: string;
  op_key: string;
  status: 'pending' | 'claimed' | 'done' | 'failed';
  enqueued_at: string;
  reported_at: string | null;
}

// ── Helpers ─────────────────────────────────────────────────────────────
function daysUntil(iso: string): number {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return Infinity;
  return Math.ceil((t - Date.now()) / 86_400_000);
}

function relTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diff = Date.now() - t;
  const s = Math.floor(diff / 1000);
  if (s < 45) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function titleCaseTier(tier: string): string {
  return tier.replace(/\b\w/g, (c) => c.toUpperCase());
}

function orgLabel(l: LicenseRow): string {
  const name = l.organization?.name || l.org_name || 'Unknown org';
  return l.tier ? `${name} · ${titleCaseTier(l.tier)}` : name;
}

// ── Buttons (match v2 design ref) ───────────────────────────────────────
const btnSec: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  borderRadius: 8, background: 'var(--surface)', color: 'var(--text-primary)',
  border: '1px solid var(--border)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  fontFamily: 'inherit', textDecoration: 'none',
};
const btnPri: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  borderRadius: 8, background: 'var(--accent)', color: '#fff',
  border: '1px solid var(--accent)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  fontFamily: 'inherit', textDecoration: 'none',
};

// ── CHANGE 2: Metric groups ─────────────────────────────────────────────
interface Metric { label: string; value: string | number; color?: string; sub?: string; }

function MetricGroup({
  icon: IconCmp, label, color, metrics,
}: {
  icon: typeof Server; label: string; color: string; metrics: Metric[];
}) {
  return (
    <Card
      style={{
        borderTop: `3px solid ${color}`, borderRadius: 12,
        boxShadow: 'var(--shadow-card)', padding: '16px 20px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <IconCmp size={15} color={color} />
        <span style={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color }}>
          {label}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${metrics.length}, 1fr)`, gap: 16 }}>
        {metrics.map((m) => (
          <div key={m.label}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>
              {m.label}
            </div>
            <div style={{ fontSize: 26, fontWeight: 700, color: m.color || 'var(--text-primary)', letterSpacing: '-.01em', lineHeight: 1 }}>
              {m.value}
            </div>
            {m.sub && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, fontWeight: 500 }}>{m.sub}</div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── CHANGE 3: 7-day validation bar chart ────────────────────────────────
function ValidationBarChart({ data }: { data: Array<{ date: string; count: number }> }) {
  if (data.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: 'var(--text-secondary)', fontSize: 14 }}>
        No validation data yet
      </div>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
        <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 11, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} allowDecimals={false} />
        <Tooltip
          cursor={{ fill: 'var(--bg-muted)' }}
          contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border)' }}
          labelStyle={{ color: 'var(--text-secondary)' }}
          formatter={(v: number) => [v, 'Validations']}
        />
        <Bar dataKey="count" radius={[3, 3, 0, 0]} minPointSize={4}>
          {data.map((d, i) => (
            <Cell key={i} fill={d.count > 0 ? 'var(--accent)' : 'var(--border)'} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── CHANGE 4: Live activity feed ────────────────────────────────────────
interface Activity {
  key: string;
  icon: typeof CheckCircle;
  color: string;
  title: string;
  sub: string;
  time: number; // epoch ms for sorting
}

const ICON_BY_TYPE = {
  done: { icon: CheckCircle, color: 'var(--status-active)' },
  validated: { icon: CheckCircle, color: 'var(--status-active)' },
  failed: { icon: XCircle, color: 'var(--status-expired)' },
  expiring: { icon: Clock, color: '#D97706' },
  dispatched: { icon: RefreshIcon, color: 'var(--accent)' },
} as const;

function ActivityFeed({ items }: { items: Activity[] }) {
  if (items.length === 0) {
    return (
      <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
        No recent activity.
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {items.map((a, i) => {
        const IconCmp = a.icon;
        return (
          <div
            key={a.key}
            style={{
              display: 'flex', gap: 12, padding: '10px 0', alignItems: 'flex-start',
              borderBottom: i < items.length - 1 ? '1px solid var(--border)' : 'none',
            }}
          >
            <IconCmp size={14} color={a.color} style={{ marginTop: 2, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {a.title}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 1 }}>{a.sub}</div>
            </div>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap', paddingTop: 2 }}>
              {relTime(new Date(a.time).toISOString())}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────
export function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [fleet, setFleet] = useState<FleetOverview | null>(null);
  const [validations, setValidations] = useState<ValidationDay[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [licenses, setLicenses] = useState<LicenseRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // 18H §4.1 Dashboard delta — the saas ledger, loaded/rendered SEPARATELY
  // from the fleet ledger above (D-34: never blended into one number).
  const [saasDetails, setSaasDetails] = useState<SaasLicenseDetailsRow[]>([]);
  const [orgMetrics, setOrgMetrics] = useState<TrustOrgMetricsRow[]>([]);
  const [opportunities, setOpportunities] = useState<TrustUpsellOpportunityRow[]>([]);
  const [saasError, setSaasError] = useState<string | null>(null);
  const [saasLoading, setSaasLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsData, fleetData, validationsData, jobsData, licensesData] = await Promise.all([
        apiRequest<DashboardStats>('/analytics/dashboard'),
        apiRequest<FleetOverview>('/fleet').catch(() => null),
        apiRequest<ValidationDay[]>('/analytics/validations?days=7').catch(() => []),
        apiRequest<Job[]>('/operations/jobs').catch(() => []),
        apiRequest<{ data?: LicenseRow[] }>('/licenses?pageSize=200').catch(() => ({ data: [] })),
      ]);
      setStats(statsData);
      setFleet(fleetData);
      setValidations(validationsData || []);
      setJobs(jobsData || []);
      setLicenses((licensesData && licensesData.data) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
      // Never fabricate numbers on error.
      setStats(null);
      setFleet(null);
      setValidations([]);
      setJobs([]);
      setLicenses([]);
    } finally {
      setLoading(false);
    }
  };

  // Independent load (partial-failure isolated from the fleet/licensing panel
  // above it) — a saas-ledger hiccup never nukes the install/platform KPIs.
  const loadSaasData = async () => {
    setSaasLoading(true);
    setSaasError(null);
    try {
      const [details, metrics, opps] = await Promise.all([
        getSaasDetails(),
        getOrgMetrics(),
        getOpportunities(),
      ]);
      setSaasDetails(details || []);
      setOrgMetrics(metrics || []);
      setOpportunities(opps || []);
    } catch (err) {
      setSaasDetails([]);
      setOrgMetrics([]);
      setOpportunities([]);
      setSaasError(err instanceof Error ? err.message : 'Failed to load trust SaaS metrics');
    } finally {
      setSaasLoading(false);
    }
  };

  useEffect(() => { loadData(); loadSaasData(); }, []);

  // ── Derived: chart data (oldest → newest, 7 days) ─────────────────────
  const chartData = useMemo(
    () =>
      [...validations]
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
        .slice(-7)
        .map((d) => ({
          date: new Date(d.date).toLocaleDateString('en-US', { weekday: 'short' }),
          count: d.validation_count,
        })),
    [validations],
  );
  const chartTotal = useMemo(() => chartData.reduce((s, d) => s + d.count, 0), [chartData]);

  // ── Derived: failed jobs in last 24h ──────────────────────────────────
  const failed24h = useMemo(
    () =>
      jobs.filter((j) => {
        if (j.status !== 'failed') return false;
        const ref = j.reported_at || j.enqueued_at;
        return Date.now() - new Date(ref).getTime() <= 86_400_000;
      }),
    [jobs],
  );

  const pendingOps = useMemo(
    () => jobs.filter((j) => j.status === 'pending' || j.status === 'claimed').length,
    [jobs],
  );

  // ── Derived: active licenses expiring within 30 days ──────────────────
  const expiringSoon = useMemo(
    () =>
      licenses
        .filter((l) => l.status === 'active' && l.expires_at)
        .map((l) => ({ l, d: daysUntil(l.expires_at as string) }))
        .filter((x) => x.d >= 0 && x.d <= 30)
        .sort((a, b) => a.d - b.d),
    [licenses],
  );

  // ── Derived: attention banner pieces ──────────────────────────────────
  const attentionItems = useMemo(() => {
    const parts: React.ReactNode[] = [];
    if (expiringSoon.length > 0) {
      const first = expiringSoon[0];
      const n = expiringSoon.length;
      parts.push(
        <span key="expiring">
          <strong>{n} license{n !== 1 ? 's' : ''} expire{n === 1 ? 's' : ''} in {first.d} day{first.d !== 1 ? 's' : ''}</strong>
          {' '}— {orgLabel(first.l)}.{' '}
          <Link to="/licenses?status=expiring" style={{ fontWeight: 700, color: 'inherit', textDecoration: 'underline' }}>
            Renew now →
          </Link>
        </span>,
      );
    }
    if (failed24h.length > 0) {
      const n = failed24h.length;
      parts.push(
        <span key="failed">
          <strong>{n} failed job{n !== 1 ? 's' : ''}</strong> in the last 24h.{' '}
          <Link to="/remote-ops" style={{ fontWeight: 700, color: 'inherit', textDecoration: 'underline' }}>
            Review →
          </Link>
        </span>,
      );
    }
    return parts;
  }, [expiringSoon, failed24h]);

  const showBanner = !bannerDismissed && attentionItems.length > 0;

  // ── Derived: merged live activity feed ────────────────────────────────
  const activity = useMemo<Activity[]>(() => {
    const out: Activity[] = [];

    for (const j of jobs) {
      const ref = j.reported_at || j.enqueued_at;
      const time = new Date(ref).getTime();
      if (Number.isNaN(time)) continue;
      if (j.status === 'done') {
        out.push({ key: `job-${j.id}`, ...ICON_BY_TYPE.done, title: `${j.op_key} completed`, sub: j.instance_id, time });
      } else if (j.status === 'failed') {
        out.push({ key: `job-${j.id}`, ...ICON_BY_TYPE.failed, title: `${j.op_key} failed`, sub: j.instance_id, time });
      } else {
        out.push({ key: `job-${j.id}`, ...ICON_BY_TYPE.dispatched, title: `${j.op_key} dispatched`, sub: `${j.instance_id} · ${j.status}`, time });
      }
    }

    // Expiring-license events (soonest first already; render as activity rows).
    for (const x of expiringSoon.slice(0, 4)) {
      const time = new Date(x.l.expires_at as string).getTime();
      out.push({
        key: `lic-${x.l.id}`, ...ICON_BY_TYPE.expiring,
        title: `License expires in ${x.d} day${x.d !== 1 ? 's' : ''}`,
        sub: orgLabel(x.l), time,
      });
    }

    return out.sort((a, b) => b.time - a.time).slice(0, 8);
  }, [jobs, expiringSoon]);

  // ── Trust SaaS ledger (D-34: kept structurally separate from fleet above) ─
  const metricsByLicense = useMemo(() => {
    const m = new Map<string, TrustOrgMetricsRow>();
    for (const r of orgMetrics) m.set(r.license_id, r);
    return m;
  }, [orgMetrics]);

  // Funnel (18E §4.1: seeded/claimed/requested → activated → engaged →
  // opportunity → converted). Built only from real, derivable fields — stage
  // names reflect exactly what's measured rather than inventing a distinct
  // "claimed" count the license server has no column for.
  const funnel = useMemo(() => {
    const provisioned = saasDetails.length;
    let published = 0, engaged = 0;
    for (const d of saasDetails) {
      const m = metricsByLicense.get(d.license_id);
      if (m?.metrics.published) published++;
      if (m && (m.metrics.ring1_views > 0 || m.metrics.requests_received > 0)) engaged++;
    }
    const oppLicenses = new Set(opportunities.filter((o) => o.state !== 'dismissed').map((o) => o.license_id));
    const convertedLicenses = new Set(saasDetails.filter((d) => d.tier !== 'free').map((d) => d.license_id));
    return [
      { label: 'Provisioned', value: provisioned },
      { label: 'Published', value: published },
      { label: 'Engaged', value: engaged },
      { label: 'Opportunity', value: oppLicenses.size },
      { label: 'Converted (paid)', value: convertedLicenses.size },
    ];
  }, [saasDetails, metricsByLicense, opportunities]);

  const funnelBySource = useMemo(() => {
    const buckets: Record<string, number> = {};
    for (const d of saasDetails) {
      const src = d.acquisition_source || 'unattributed';
      buckets[src] = (buckets[src] || 0) + 1;
    }
    return Object.entries(buckets).sort((a, b) => b[1] - a[1]);
  }, [saasDetails]);

  // Telemetry-health (rollup silence): a provisioned saas org whose latest
  // ingest is >48h old (or has never ingested at all) — the daily
  // /telemetry/trust push has gone quiet for it.
  const rollupSilent = useMemo(() => {
    return saasDetails.filter((d) => {
      const m = metricsByLicense.get(d.license_id);
      if (!m) return true;
      return Date.now() - new Date(m.ingested_at).getTime() > 48 * 3600 * 1000;
    });
  }, [saasDetails, metricsByLicense]);

  const paidTierCount = useMemo(() => saasDetails.filter((d) => d.tier !== 'free').length, [saasDetails]);
  const openOpportunityCount = useMemo(() => opportunities.filter((o) => o.state === 'open' || o.state === 'nudged').length, [opportunities]);

  // ── Metric values (real data; "—"/0 when missing) ─────────────────────
  const installs = fleet?.totals.instances ?? 0;
  const healthy = fleet?.totals.healthy ?? 0;
  const version =
    fleet && fleet.version_distribution.length
      ? [...fleet.version_distribution].sort((a, b) => b.count - a.count)[0].version
      : '—';

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
        <div style={{ textAlign: 'center' }}>
          <div className="animate-spin" style={{ width: 32, height: 32, border: '2px solid var(--accent)', borderTopColor: 'transparent', borderRadius: '50%', margin: '0 auto 12px' }} />
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard Overview</h1>
          <p className="page-subtitle">{PRODUCT_NAME} License Server — Real-time status</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={loadData} style={btnSec}>
            <RefreshCw size={13} /> Refresh
          </button>
          <Link to="/licenses/new" style={btnPri}>
            <Plus size={13} /> Issue License
          </Link>
        </div>
      </div>

      {/* Load error (distinct from attention banner) */}
      {error && (
        <div style={{ marginBottom: 20 }}>
          <AlertBanner kind="error">Unable to load dashboard data: {error}</AlertBanner>
        </div>
      )}

      {/* CHANGE 1: Attention Required banner */}
      {showBanner && (
        <div style={{ marginBottom: 20 }}>
          <AlertBanner kind="warning" onDismiss={() => setBannerDismissed(true)}>
            {attentionItems.map((node, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span style={{ margin: '0 12px', color: '#D97706' }}>·</span>}
                {node}
              </React.Fragment>
            ))}
          </AlertBanner>
        </div>
      )}

      {/* CHANGE 2: 4 semantic metric groups */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 22 }}>
        <MetricGroup
          icon={Server} label="Fleet" color="#0891B2"
          metrics={[
            { label: 'Installs', value: installs },
            { label: 'Healthy', value: healthy, color: healthy === installs && installs > 0 ? 'var(--status-active)' : healthy < installs ? '#D97706' : 'var(--text-primary)' },
            { label: 'Version', value: version, color: 'var(--text-secondary)' },
          ]}
        />
        <MetricGroup
          icon={Key} label="Licensing" color="var(--accent)"
          metrics={[
            { label: 'Active', value: stats?.activeLicenses ?? 0, color: 'var(--status-active)' },
            { label: 'Trial', value: stats?.trialLicenses ?? 0, color: 'var(--status-trial)' },
            { label: 'At Risk', value: expiringSoon.length, color: expiringSoon.length > 0 ? '#D97706' : 'var(--text-primary)' },
          ]}
        />
        <MetricGroup
          icon={CreditCard} label="Revenue" color="#059669"
          metrics={[
            { label: 'MRR', value: `$${(stats?.monthlyRevenue ?? 0).toLocaleString()}` },
            { label: 'Validations', value: stats?.validationsToday ?? 0, sub: 'today' },
          ]}
        />
        <MetricGroup
          icon={Terminal} label="Operations" color="#7C3AED"
          metrics={[
            { label: 'Pending', value: pendingOps, color: pendingOps > 0 ? '#D97706' : 'var(--text-primary)' },
            { label: 'Failed', value: failed24h.length, color: failed24h.length > 0 ? 'var(--status-expired)' : 'var(--text-primary)', sub: 'last 24h' },
          ]}
        />
      </div>

      {/* 18H §4.1 Dashboard delta — the trust SaaS ledger. Deliberately its
          OWN row using the shared Kpi tile (not MetricGroup above) so it never
          reads as "one number" blended with fleet/install metrics (D-34). */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Trust SaaS ledger</span>
          <Link to="/sales/trust/org-metrics" style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
            View all <ArrowRight size={12} />
          </Link>
        </div>
        {saasError && (
          <div style={{ marginBottom: 10 }}>
            <AlertBanner kind="error">Unable to load trust SaaS metrics: {saasError}</AlertBanner>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: rollupSilent.length > 0 ? 10 : 0 }}>
          <Kpi label="Trust orgs" value={saasLoading ? '—' : saasDetails.length} icon="cloud" accent="#4F46E5" />
          <Kpi label="Activated" value={saasLoading ? '—' : funnel[1].value} icon="check-circle" accent="#16A34A" hint={saasLoading ? undefined : `${saasDetails.length > 0 ? Math.round((funnel[1].value / saasDetails.length) * 100) : 0}% of provisioned`} />
          <Kpi label="Paid tier" value={saasLoading ? '—' : paidTierCount} icon="credit-card" accent="#059669" hint="trust_base+" />
          <Kpi label="Open opportunities" value={saasLoading ? '—' : openOpportunityCount} icon="trending-up" accent={openOpportunityCount > 0 ? '#D97706' : undefined} />
        </div>
        {!saasLoading && rollupSilent.length > 0 && (
          <div className="card" style={{ padding: 0 }}>
            <AttnRow
              icon="wifi-off" color="#D97706"
              action="View org metrics" onAction={() => navigate('/sales/trust/org-metrics')}
            >
              <strong>{rollupSilent.length} trust org{rollupSilent.length !== 1 ? 's' : ''}</strong> {rollupSilent.length === 1 ? 'has' : 'have'} gone quiet — no daily telemetry rollup ingested in the last 48h.
            </AttnRow>
          </div>
        )}
      </div>

      {/* Funnel (18E §4.1): seeded/provisioned → activated → engaged →
          opportunity → converted, segmented by acquisition_source. */}
      {!saasLoading && saasDetails.length > 0 && (
        <div className="card" style={{ marginBottom: 16, overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>Trust SaaS funnel</span>
          </div>
          <div style={{ padding: '18px 20px', display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 24 }}>
            <div>
              {funnel.map((stage, i) => {
                const pct = funnel[0].value > 0 ? Math.round((stage.value / funnel[0].value) * 100) : 0;
                return (
                  <div key={stage.label} style={{ marginBottom: i < funnel.length - 1 ? 12 : 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 4 }}>
                      <span style={{ color: 'var(--text-secondary)' }}>{stage.label}</span>
                      <span style={{ fontWeight: 600 }}>{stage.value} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({pct}%)</span></span>
                    </div>
                    <div style={{ height: 6, background: 'var(--bg-muted)', borderRadius: 99 }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: 'var(--accent)', borderRadius: 99, transition: 'width .3s' }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 10 }}>By acquisition source</div>
              {funnelBySource.length === 0 ? (
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>No data yet.</div>
              ) : funnelBySource.map(([source, count]) => (
                <div key={source} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ textTransform: 'capitalize', color: 'var(--text-secondary)' }}>{source.replace(/_/g, ' ')}</span>
                  <span style={{ fontWeight: 600 }}>{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Charts + License Status row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* CHANGE 3: 7-day bar chart */}
        <Card style={{ borderRadius: 12, boxShadow: 'var(--shadow-card)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>License Validations — Last 7 days</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{chartTotal} total</span>
          </div>
          <div style={{ padding: '18px 20px' }} data-testid="validation-bar-chart">
            <ValidationBarChart data={chartData} />
          </div>
        </Card>

        {/* License Status — kept from current */}
        <Card style={{ borderRadius: 12, boxShadow: 'var(--shadow-card)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>License Status</span>
          </div>
          <div style={{ padding: '16px 20px' }}>
            {[
              { label: 'Active', count: stats?.activeLicenses ?? 0, color: 'var(--status-active)' },
              { label: 'Trial', count: stats?.trialLicenses ?? 0, color: 'var(--status-trial)' },
              { label: 'Expired', count: stats?.expiredLicenses ?? 0, color: 'var(--status-expired)' },
              { label: 'Suspended', count: stats?.suspendedLicenses ?? 0, color: 'var(--status-suspended)' },
              { label: 'Revoked', count: stats?.revokedLicenses ?? 0, color: 'var(--text-muted)' },
            ].map(({ label, count, color }) => {
              const total = stats?.totalLicenses || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={label} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
                    <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
                    <span style={{ fontWeight: 700 }}>{count} ({pct}%)</span>
                  </div>
                  <div style={{ height: 5, background: 'var(--bg-muted)', borderRadius: 99 }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 99 }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* CHANGE 4: Live activity feed */}
      <Card style={{ borderRadius: 12, boxShadow: 'var(--shadow-card)', overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>Live Activity</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: 'var(--text-secondary)' }}>
              <span className="animate-pulse" style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--status-active)' }} />
              Live
            </span>
          </div>
          <Link to="/audit-logs" style={{ fontSize: 12.5, color: 'var(--accent)', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}>
            View audit log <ArrowRight size={12} />
          </Link>
        </div>
        <div style={{ padding: '4px 20px 8px' }}>
          <ActivityFeed items={activity} />
        </div>
      </Card>
    </div>
  );
}
